import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, MapPin } from "lucide-react";
import { scrollToId } from "@/components/Logo";
import { WhatsAppIcon, WHATSAPP_URL } from "@/components/WhatsApp";

const HERO_IMG =
  "https://images.unsplash.com/photo-1730742298439-6d82f9edc3c2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbHV4dXJ5JTIwc3BvcnRzJTIwY2FyJTIwaGVhZGxpZ2h0JTIwZGV0YWlsfGVufDB8fHx8MTc5MjgzMDY5Nnww&ixlib=rb-4.1.0&q=85";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const metrics = [
  { value: "24H", label: "Servicio todos los días" },
  { value: "98%", label: "Piezas localizadas" },
  { value: "<2h", label: "Respuesta media" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.15]);

  return (
    <section id="hero" ref={ref} className="relative min-h-screen flex flex-col justify-end overflow-hidden noise-overlay" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0 -z-10">
        <img src={HERO_IMG} alt="Detalle de faro de coche deportivo" className="w-full h-[120%] object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-t from-industrial-950 via-industrial-950/70 to-industrial-950/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-industrial-950/90 via-transparent to-industrial-950/60" />
      </motion.div>

      <motion.div style={{ opacity }} className="max-w-7xl mx-auto w-full px-4 sm:px-6 pt-36 pb-10">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="font-mono text-xs uppercase tracking-[0.3em] text-brand-amber flex items-center gap-2 mb-6"
          data-testid="hero-eyebrow"
        >
          <MapPin className="w-3.5 h-3.5" /> Tu pides la pieza, nosotros la buscamos por ti · Aljarafe, Sevilla
        </motion.p>

        <h1 className="font-display font-black uppercase leading-[0.88] tracking-tight text-[13vw] sm:text-7xl md:text-8xl lg:text-[7.5rem]" data-testid="hero-headline">
          <MaskedLine delay={0.3}>Buscamos la pieza</MaskedLine>
          <MaskedLine delay={0.42} className="text-outline">exacta</MaskedLine>
          <MaskedLine delay={0.54}>para tu vehículo<span className="text-brand-orange">.</span></MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-6 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
          data-testid="hero-subheadline"
        >
          Accedemos a la mayor red de desguaces y proveedores verificados del país.
          Dinos qué recambio necesitas y recibe presupuesto garantizado, sin compromiso y sin registro.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollToId("#presupuesto")}
            data-testid="hero-cta-button"
            className="bg-brand-orange hover:bg-brand-amber text-white font-display font-bold uppercase tracking-widest text-base px-8 py-4 rounded-sm transition-all duration-300 hover:translate-y-[-2px]"
          >
            Solicitar presupuesto
          </button>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-button"
            className="border border-[#25D366]/60 text-[#25D366] hover:bg-[#25D366]/10 font-mono text-xs uppercase tracking-widest px-6 py-4 rounded-sm transition-colors duration-300 flex items-center gap-2"
          >
            <WhatsAppIcon className="w-4 h-4" /> WhatsApp 644 856 072
          </a>
          <button
            onClick={() => scrollToId("#como-funciona")}
            data-testid="hero-secondary-button"
            className="border border-industrial-600 hover:border-brand-amber text-slate-200 hover:text-brand-amber font-mono text-xs uppercase tracking-widest px-6 py-4 rounded-sm transition-colors duration-300"
          >
            Cómo funciona
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-14 grid grid-cols-3 border-t border-industrial-700/70"
          data-testid="hero-metrics"
        >
          {metrics.map((m) => (
            <div key={m.label} className="pt-5 pr-4">
              <p className="font-display font-black text-3xl sm:text-5xl text-brand-amber">{m.value}</p>
              <p className="font-mono text-[10px] sm:text-xs uppercase tracking-widest text-slate-400 mt-1">{m.label}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
        className="absolute bottom-6 right-6 hidden lg:block text-slate-500"
      >
        <ChevronDown className="w-6 h-6" />
      </motion.div>
    </section>
  );
};
